import React, { useEffect, useState } from 'react';
import { Text, View } from "../../../src/Components/Themed";
import type { BestAvailableOrderProps, MarketProps, MarketSideOptionProps, OrderProps, SupportedMarketProps, TeamProps, TournamentProps, TradeProps } from '../../../src/types';
import { MarketComponentApi, MarketComponentHelpers } from '../../../src/MarketComponents/api';
import { ActivityIndicator, FlatList } from 'react-native';
import { useColors } from '../../../src/constants/useColors';
import { TeamImage } from '../../../src/Components/Jerseys';
import MarketButton from '../../../src/MarketComponents/components/MarketButton';
import { MarketButtonHelpers } from '../../../src/MarketComponents/components/MarketButton/api';

type TeamFutureCardProps = {
    team_id:string,
    markets:MarketProps[],
    header_style?:any,
    best_available_orders:{[key:string]:BestAvailableOrderProps[]},
    onOrder:(order:OrderProps, tournament:TournamentProps) => void
}

const TeamFutureCard = ({ team_id, markets, best_available_orders, onOrder, header_style }:TeamFutureCardProps) => {
    const Colors = useColors()
    const [ card_state, setCardState ] = useState<{
        loading:boolean,
        team?:TeamProps,
        tournaments:TournamentProps[],
        latest_trades:TradeProps[]
    }>({
        loading: false,
        tournaments:[],
        latest_trades:[]
    });
    const { loading, team, tournaments, latest_trades } = card_state;
    useEffect(() => {
        if(!team_id){ return }
        getData(team_id)
    },[team_id])

    const getData = async(id:string) => {
        setCardState({ ...card_state, loading: true });
        const tms = await MarketComponentApi.getTeamsByIds([id]);

        const tourneys = await MarketComponentApi.getScheduledTournamentsByTeams(id);
        const trades = await MarketComponentApi.getLatestTradesByEvents('tournament', tourneys.map(t => t.tournament_id));
        setCardState({
            ...card_state,
            loading:false,
            team: tms[0],
            latest_trades: trades,
            tournaments:tourneys
        });
    }

    const renderSides = (data:{ item:MarketSideOptionProps, index:number, market:MarketProps, tournament:TournamentProps, trades:TradeProps[], ba:BestAvailableOrderProps[] }) => {
        if(!team){ return <></> }
        const is_locked = MarketComponentHelpers.isTournamentMarketLocked(data.tournament, data.market.market_id)
        const ba_order = data.ba.find(o => o.market_id == data.market.market_id && o.side_type == 'team' && o.side_id == team.team_id && o.side == data.item.side && o.show);
        const side_details = MarketComponentHelpers.getTournamentSideIds(data.market, data.item, undefined, team);
        if(!side_details){ return <></> }
        let trade = MarketButtonHelpers.getVisibleTrade('best_available', data.market, data.item, data.trades, data.ba, side_details.side_id, side_details.reversed_side_id); 
        if(!trade){
            trade = MarketButtonHelpers.getTradeFromMarket(data.market, data.tournament.tournament_id, 'team', data.item.side, data.item.id_source, side_details.side_id, side_details.reversed_side_id);
        }
        let disabled = false
        return (
            <View key={`${data.tournament.tournament_id}_${team.team_id}_${data.item.side}`} style={{ margin:2, height:50, width:75 }}>
                <MarketButton
                    side_option={data.item}
                    market={data.market}
                    trade={trade}
                    locked={is_locked || disabled}
                    show_grades={false}
                    order={ba_order}
                    onLongPress={() => console.log('')}
                    onPress={(order) => {
                        const order_title = MarketComponentHelpers.getOrderTitleForTournament(order, data.market, data.tournament, undefined, team);
                        if(!order_title){ return alert('Unable to process order') }
                        onOrder({ ...order, title: order_title, league_id: data.tournament.league_id }, data.tournament)
                    }}
                />
            </View>
        )
    }



    const renderMarkets = (data:{ item:SupportedMarketProps, index:number, tournament:TournamentProps }) => {
        if(!team){ return <></> }
        const tournament_trades = latest_trades.filter(t => t.event_id == data.tournament.tournament_id && t.market_type == 'FOR_MONEY' && t.side_type == 'team' && t.side_id == team.team_id && t.market_id == data.item.market_id);
        const market = markets.find(mk => mk.market_id == data.item.market_id)
        const tournament_ba = best_available_orders[`tournament:${data.tournament.tournament_id}`] ?? []
        const market_ba = tournament_ba.filter(ba => ba.market_id == data.item.market_id && ba.side_type == 'team' && ba.side_id == team_id);
        if(!market){ return <></> }
        return (
            <View transparent style={{ flexDirection:'row', alignItems:'center', borderBottomWidth:1, borderColor:Colors.borders.light }}>
                <View transparent style={{ flex:1, padding:10 }}>
                    <Text theme='h2'>{data.tournament.tournament_name}</Text>
                </View>
                <View transparent nativeID='market_sides' style={{ flexDirection:'row', justifyContent:'center', alignItems:'center', padding:10 }}>
                    {market.side_options.sort((a,b) => {
                        let a_t = a.side == market.trade_side ? 1 : 0
                        let b_t = b.side == market.trade_side ? 1 : 0
                        return a_t - b_t
                    }).map((so, index) => {
                        return renderSides({ item:so, index, market, tournament:data.tournament, trades:tournament_trades, ba:market_ba })
                    })}
                </View>
            </View>
        )
    }

    const renderTournaments = (data:{ item:TournamentProps, index:number }) => {
        if(!data.item.supported_markets){ return <></> }
        return (
            <View transparent>
                <FlatList
                    data={data.item.supported_markets}
                    renderItem={({ item, index }) => renderMarkets({ item, index, tournament:data.item })}
                />
            </View>
        )
    }
    if(!team){ return <></> }

    return (
        <View transparent>
            <View type='header' style={{ flexDirection:'row', alignItems:'center', padding:10, ...header_style }}>
                <TeamImage
                    team={team}
                    size={14}
                />
                <View transparent style={{ flex:1, marginLeft:10 }}>
                    <Text theme='h2'>{team.market_name} {team.name}</Text>
                    <Text theme='description' style={{ marginTop:3 }}>{team.sub_division ?? team.division}</Text>
                </View>
            </View>
            <View transparent>
                {loading ?
                <ActivityIndicator size={18} color={Colors.text.h1} />
                :<></>}
                <FlatList
                    data={tournaments}
                    renderItem={renderTournaments}
                    keyExtractor={item => item.tournament_id.toString()}
                    key={'tournament_list'}
                />
            </View>
            
        </View>
    )
}

export default TeamFutureCard


/*
 <TeamTournamentMarket
                team={team}
                style={{flex:1}}
                show_event
                direction='horizontal'
                image_size={14}
                tournament={data.tournament}
                latest_trades={tournament_trades}
                hide_liquidity
                hide_stat_banner
                best_available_orders={[]}
                onOrder={(o) => onOrder(o, data.tournament)}
                event_type='tournament'
                default_price_view='best_available'
                onView={() => console.log('')}
                market={market}
                event_order_stats={[]}
                onTradeLongPress={() => console.log('')}
            />
*/